import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { userEndpoint, Base_URL } from "actions/API_endpoint";
import Swal from "sweetalert2";

export default function ProjectDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [evalData, setEvalData] = useState({
        evaluation: "satisfactory",
        eval_month: "January",
        eval_year: new Date().getFullYear(),
        comment: "",
        stipend: "",
        stipend_status: "pending"
    });
    const [editingEvalId, setEditingEvalId] = useState(null);

    const fetchProject = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${Base_URL}${userEndpoint.getProjectById(id)}`);
            const data = await response.json();
            if (data.success) {
                setProject(data.data);
            } else {
                Swal.fire("Error", "Project not found", "error");
                navigate("/admin/projects");
            }
        } catch (error) {
            console.error("Error fetching project:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProject();
    }, [id]);

    const handleEvalSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = { ...evalData };
            if (project.scheme === 'mpdi') {
                submitData.stipend = submitData.stipend ? parseInt(submitData.stipend) : null;
            }

            const url = editingEvalId 
                ? `${Base_URL}${userEndpoint.updateProjectEvaluation(id, editingEvalId)}`
                : `${Base_URL}${userEndpoint.addProjectEvaluation(id)}`;
            
            const method = editingEvalId ? "PUT" : "POST";

            const response = await fetch(url, {
                method: method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(submitData)
            });

            const data = await response.json();
            if (data.success) {
                Swal.fire("Success", editingEvalId ? "Evaluation updated" : "Evaluation added", "success");
                setShowModal(false);
                setEditingEvalId(null);
                fetchProject(); // refresh data
            } else {
                Swal.fire("Error", data.error || `Failed to ${editingEvalId ? 'update' : 'add'} evaluation`, "error");
            }
        } catch (error) {
            Swal.fire("Error", "Network error", "error");
        }
    };

    const handleEditEval = (ev) => {
        setEvalData({
            evaluation: ev.evaluation,
            eval_month: ev.eval_month,
            eval_year: ev.eval_year,
            comment: ev.comment || "",
            stipend: ev.stipend || "",
            stipend_status: ev.stipend_status || "pending"
        });
        setEditingEvalId(ev.id);
        setShowModal(true);
    };

    const handleDeleteEval = async (evalId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "This evaluation will be deleted permanently.",
            icon: 'warning',
            background: '#0f2847',
            color: '#fff',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3b82f6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${Base_URL}${userEndpoint.deleteProjectEvaluation(id, evalId)}`, {
                        method: "DELETE"
                    });
                    const data = await response.json();
                    if (data.success) {
                        Swal.fire({
                            title: 'Deleted!',
                            text: 'Evaluation has been deleted.',
                            icon: 'success',
                            background: '#0f2847',
                            color: '#fff'
                        });
                        fetchProject();
                    } else {
                        Swal.fire({
                            title: 'Error',
                            text: data.error || "Failed to delete evaluation",
                            icon: 'error',
                            background: '#0f2847',
                            color: '#fff'
                        });
                    }
                } catch (error) {
                    Swal.fire({
                        title: 'Error',
                        text: 'Network error',
                        icon: 'error',
                        background: '#0f2847',
                        color: '#fff'
                    });
                }
            }
        });
    };

    if (loading || !project) return <div className="text-white mt-10 ml-4">Loading...</div>;

    return (
        <div className="flex flex-wrap mt-4">
            <div className="w-full xl:w-4/12 px-4">
                <div className="relative flex flex-col min-w-0 break-words bg-dark-secondary w-full mb-6 shadow-xl rounded-lg">
                    <div className="px-6 py-6">
                        <div className="text-center mt-4">
                            <h3 className="text-xl font-semibold leading-normal mb-2 text-white">
                                {project.project_name}
                            </h3>
                            <div className="text-sm leading-normal mt-0 mb-2 text-gray-400 font-bold uppercase">
                                <i className="fas fa-layer-group mr-2 text-lg text-gray-500"></i>
                                Scheme: {project.scheme}
                            </div>
                            <div className="mb-2 text-gray-300 mt-4">
                                <i className="fas fa-user mr-2 text-lg text-gray-500"></i>
                                Leader: {project.team_leader_data ? `${project.team_leader_data.first_name} ${project.team_leader_data.last_name}` : "Unknown"}
                            </div>
                            <div className="mb-2 text-gray-300 mt-2">
                                <i className="fas fa-chalkboard-teacher mr-2 text-lg text-gray-500"></i>
                                Mentor: {project.mentor_name || "N/A"}
                            </div>
                            <div className="mb-2 text-gray-300 mt-2">
                                <i className="fas fa-toggle-on mr-2 text-lg text-gray-500"></i>
                                Status: <span className={project.status === 'active' ? 'text-green-400 bg-green-500/10 px-2 py-1 object-cover items-center rounded-xl text-xs uppercase' : 'text-red-400 bg-red-500/10 px-2 py-1 object-cover items-center rounded-xl text-xs uppercase'}>{project.status}</span>
                            </div>
                            <div className="mb-2 text-gray-300 mt-2">
                                <i className="fas fa-calendar-alt mr-2 text-lg text-gray-500"></i>
                                Semester Registered: <span className="text-gray-400">{project.Semester_Registered || "N/A"}</span>
                            </div>
                            <div className="mb-2 text-gray-300 mt-2">
                                <i className="fas fa-calendar-check mr-2 text-lg text-gray-500"></i>
                                Academic Year: <span className="text-gray-400">{project.Academic_Year || "N/A"}</span>
                            </div>
                        </div>
                        <div className="mt-10 py-4 border-t border-dark-tertiary text-center">
                            <div className="flex flex-wrap justify-center">
                                <div className="w-full px-4 text-left">
                                    <h4 className="font-bold text-gray-300 uppercase text-sm mb-3">Team Members</h4>
                                    <ul className="text-gray-400 space-y-2 text-sm">
                                        {project.team_members_data && project.team_members_data.length > 0 ? (
                                            project.team_members_data.map((m, i) => (
                                                <li key={i}><i className="fas fa-check text-green-500 mr-2"></i>{m ? `${m.first_name} ${m.last_name} (${m.admission_no})` : 'Unknown User'}</li>
                                            ))
                                        ) : (
                                            <li>No additional members</li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full xl:w-8/12 px-4">
                <div className="relative flex flex-col min-w-0 break-words bg-dark-secondary w-full mb-6 shadow-lg rounded">
                    <div className="rounded-t mb-0 px-4 py-3 border-0 bg-dark-tertiary">
                        <div className="flex flex-wrap items-center">
                            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
                                <h3 className="font-bold text-lg text-white">
                                    Evaluation History
                                </h3>
                            </div>
                            <div className="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
                                <button
                                    onClick={() => {
                                        setEditingEvalId(null);
                                        setEvalData({
                                            evaluation: "satisfactory",
                                            eval_month: "January",
                                            eval_year: new Date().getFullYear(),
                                            comment: "",
                                            stipend: "",
                                            stipend_status: "pending"
                                        });
                                        setShowModal(true);
                                    }}
                                    className="bg-accent-primary hover:bg-accent-hover text-white active:bg-blue-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
                                >
                                    <i className="fas fa-plus mr-1"></i> Add Evaluation
                                </button>
                                <button
                                    onClick={() => navigate('/admin/projects')}
                                    className="bg-gray-600 hover:bg-gray-500 text-white active:bg-gray-700 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
                                >
                                    Back
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="block w-full overflow-x-auto">
                        <table className="items-center w-full bg-transparent border-collapse">
                            <thead>
                                <tr>
                                    <th className="px-6 bg-dark-tertiary text-gray-300 align-middle border border-solid border-dark-tertiary py-3 text-xs uppercase font-semibold text-left">Month/Year</th>
                                    <th className="px-6 bg-dark-tertiary text-gray-300 align-middle border border-solid border-dark-tertiary py-3 text-xs uppercase font-semibold text-left">Evaluation</th>
                                    {project.scheme === 'mpdi' && (
                                        <>
                                            <th className="px-6 bg-dark-tertiary text-gray-300 align-middle border border-solid border-dark-tertiary py-3 text-xs uppercase font-semibold text-left">Stipend</th>
                                            <th className="px-6 bg-dark-tertiary text-gray-300 align-middle border border-solid border-dark-tertiary py-3 text-xs uppercase font-semibold text-left">Status</th>
                                        </>
                                    )}
                                    <th className="px-6 bg-dark-tertiary text-gray-300 align-middle border border-solid border-dark-tertiary py-3 text-xs uppercase font-semibold text-left">Comment</th>
                                    <th className="px-6 bg-dark-tertiary text-gray-300 align-middle border border-solid border-dark-tertiary py-3 text-xs uppercase font-semibold text-center mt-1"><i className="fas fa-cog"></i></th>
                                </tr>
                            </thead>
                            <tbody>
                                {project.evaluations && project.evaluations.length > 0 ? (
                                    [...project.evaluations].sort((a, b) => {
                                        const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                                        if (a.eval_year !== b.eval_year) {
                                            return a.eval_year - b.eval_year;
                                        }
                                        return months.indexOf(a.eval_month) - months.indexOf(b.eval_month);
                                    }).map(ev => (
                                        <tr key={ev.id} className="border-b border-dark-tertiary hover:bg-dark-tertiary/50 text-white text-sm">
                                            <td className="px-6 align-middle py-4 font-bold">{ev.eval_month} {ev.eval_year}</td>
                                            <td className="px-6 align-middle py-4">
                                                <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${ev.evaluation === 'satisfactory' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                                    {ev.evaluation}
                                                </span>
                                            </td>
                                            {project.scheme === 'mpdi' && (
                                                <>
                                                    <td className="px-6 align-middle py-4">₹{ev.stipend || 0}</td>
                                                    <td className="px-6 align-middle py-4 uppercase text-xs">{ev.stipend_status}</td>
                                                </>
                                            )}
                                            <td className="px-6 align-middle py-4 max-w-xs" title={ev.comment}>
                                                <div className="flex items-center">
                                                    <span className="truncate">{ev.comment || "-"}</span>
                                                    {ev.comment && ev.comment.length > 20 && (
                                                        <button
                                                            onClick={() => Swal.fire({
                                                                title: 'Evaluation Comment',
                                                                text: ev.comment,
                                                                icon: 'info',
                                                                background: '#0f2847',
                                                                color: '#fff',
                                                                confirmButtonColor: '#3b82f6',
                                                            })}
                                                            className="ml-2 text-accent-primary hover:text-accent-hover focus:outline-none"
                                                            title="View Full Comment"
                                                        >
                                                            <i className="fas fa-eye"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle py-4 text-center whitespace-nowrap">
                                                <button
                                                    onClick={() => handleEditEval(ev)}
                                                    className="text-blue-400 hover:text-blue-300 transition-colors p-2 mr-1"
                                                    title="Edit Evaluation"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteEval(ev.id)}
                                                    className="text-red-400 hover:text-red-300 transition-colors p-2"
                                                    title="Delete Evaluation"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={project.scheme === 'mpdi' ? 6 : 4} className="text-center py-6 text-gray-500">No evaluations yet</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Evaluation Modal */}
            {showModal && (
                <>
                    <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
                        <div className="relative w-full my-6 mx-auto max-w-lg">
                            <div className="border border-white/10 rounded-lg shadow-lg relative flex flex-col w-full bg-dark-secondary outline-none focus:outline-none">
                                <div className="flex items-start justify-between p-5 border-b border-solid border-dark-tertiary rounded-t">
                                    <h3 className="text-xl font-semibold text-white">
                                        {editingEvalId ? 'Edit Evaluation' : 'Monthly Evaluation'}
                                    </h3>
                                    <button
                                        className="p-1 ml-auto bg-transparent border-0 text-white opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                                        onClick={() => {
                                            setShowModal(false);
                                            setEditingEvalId(null);
                                        }}
                                    >
                                        <span className="text-white h-6 w-6 text-2xl block outline-none focus:outline-none">×</span>
                                    </button>
                                </div>
                                <div className="relative p-6 flex-auto">
                                    <form id="evalForm" onSubmit={handleEvalSubmit}>
                                        <div className="flex flex-wrap -mx-2 mb-4">
                                            <div className="w-1/2 px-2">
                                                <label className="block text-gray-300 text-sm font-bold mb-2">Evaluation Month</label>
                                                <select
                                                    required
                                                    value={evalData.eval_month}
                                                    onChange={(e) => setEvalData({ ...evalData, eval_month: e.target.value })}
                                                    className="w-full bg-dark-primary text-white border-0 px-3 py-2 rounded shadow focus:outline-none focus:ring"
                                                >
                                                    {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map(m => (
                                                        <option key={m} value={m}>{m}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="w-1/2 px-2">
                                                <label className="block text-gray-300 text-sm font-bold mb-2">Evaluation Year</label>
                                                <input
                                                    type="number"
                                                    required
                                                    max={new Date().getFullYear()}
                                                    value={evalData.eval_year}
                                                    onChange={(e) => setEvalData({ ...evalData, eval_year: parseInt(e.target.value) })}
                                                    className="w-full bg-dark-primary text-white border-0 px-3 py-2 rounded shadow focus:outline-none focus:ring"
                                                />
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="block text-gray-300 text-sm font-bold mb-2">Evaluation Status</label>
                                            <select
                                                required
                                                value={evalData.evaluation}
                                                onChange={(e) => setEvalData({ ...evalData, evaluation: e.target.value })}
                                                className="w-full bg-dark-primary text-white border-0 px-3 py-2 rounded shadow focus:outline-none focus:ring"
                                            >
                                                <option value="satisfactory">Satisfactory (S)</option>
                                                <option value="unsatisfactory">Unsatisfactory (X)</option>
                                            </select>
                                        </div>
                                        {project.scheme === 'mpdi' && (
                                            <>
                                                <div className="mb-4">
                                                    <label className="block text-gray-300 text-sm font-bold mb-2">Stipend Amount (₹)</label>
                                                    <input
                                                        type="number"
                                                        value={evalData.stipend}
                                                        onChange={(e) => setEvalData({ ...evalData, stipend: e.target.value })}
                                                        className="w-full bg-dark-primary text-white border-0 px-3 py-2 rounded shadow focus:outline-none focus:ring"
                                                        placeholder="e.g. 6000"
                                                    />
                                                </div>
                                                <div className="mb-4">
                                                    <label className="block text-gray-300 text-sm font-bold mb-2">Stipend Status</label>
                                                    <select
                                                        value={evalData.stipend_status}
                                                        onChange={(e) => setEvalData({ ...evalData, stipend_status: e.target.value })}
                                                        className="w-full bg-dark-primary text-white border-0 px-3 py-2 rounded shadow focus:outline-none focus:ring"
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="paid">Paid</option>
                                                    </select>
                                                </div>
                                            </>
                                        )}
                                        <div className="mb-4">
                                            <label className="block text-gray-300 text-sm font-bold mb-2">Comments</label>
                                            <textarea
                                                rows="3"
                                                value={evalData.comment}
                                                onChange={(e) => setEvalData({ ...evalData, comment: e.target.value })}
                                                className="w-full bg-dark-primary text-white border-0 px-3 py-2 rounded shadow focus:outline-none focus:ring"
                                                placeholder="Remarks regarding progress..."
                                            ></textarea>
                                        </div>
                                    </form>
                                </div>
                                <div className="flex items-center justify-end p-6 border-t border-solid border-dark-tertiary rounded-b">
                                    <button
                                        className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                                        type="button"
                                        onClick={() => {
                                            setShowModal(false);
                                            setEditingEvalId(null);
                                        }}
                                    >
                                        Close
                                    </button>
                                    <button
                                        className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                                        type="submit"
                                        form="evalForm"
                                    >
                                        {editingEvalId ? 'Update Evaluation' : 'Submit Evaluation'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="opacity-75 fixed inset-0 z-40 bg-black"></div>
                </>
            )}
        </div>
    );
}
